"use client";

import { useCreateSavedSearch } from "@/hooks/notification-hooks";
import { useGetCurrentUser } from "@/hooks/auth-hook";
import { useStore } from "@/state-management/context";
import { AnimatePresence, motion } from "framer-motion";
import { BellPlus, Check, Loader2 } from "lucide-react";
import { useLocale } from "next-intl";
import { useSearchParams } from "next/navigation";
import { useState } from "react";

export default function SaveSearchButton() {
  const locale = useLocale();
  const isAr = locale === "ar";
  const { filter } = useStore();
  const searchParams = useSearchParams();
  const { data: userData } = useGetCurrentUser();
  const create = useCreateSavedSearch();
  const [done, setDone] = useState(false);

  const isLoggedIn = !!userData?.user;

  const buildPayload = () => {
    const type = searchParams.get("type") || filter?.propertyType?.filter(Boolean)?.join(",") || "";
    const location = searchParams.get("location") || filter?.location?.filter(Boolean)?.join(",") || "";
    const tab = searchParams.get("tab") || filter?.type || "all";

    const propertyType = type ? type.split(",").filter(Boolean) : [];
    const region = location ? location.split(",").filter(Boolean) : [];
    const purpose = tab === "buy" || tab === "rent" ? tab : "all";

    const parts: string[] = [];
    if (propertyType.length) parts.push(propertyType.join("، "));
    if (region.length) parts.push(region.join("، "));
    const name = parts.join(" - ") || (isAr ? "بحث مخصص" : "Custom search");

    return {
      name,
      propertyType,
      region,
      minPrice: Number(filter?.price?.min) || undefined,
      maxPrice: Number(filter?.price?.max) || undefined,
      purpose,
      notify: true,
    };
  };

  const handleSave = () => {
    if (!isLoggedIn) {
      window.location.href = `/${locale}/auth/login`;
      return;
    }
    create.mutate(buildPayload(), {
      onSuccess: () => {
        setDone(true);
        setTimeout(() => setDone(false), 3000);
      },
    });
  };

  return (
    <button
      onClick={handleSave}
      disabled={create.isPending || done}
      dir={isAr ? "rtl" : "ltr"}
      className="group inline-flex items-center gap-2 rounded-2xl border border-[#0d3589]/15 bg-[#0d3589]/[0.04] hover:bg-[#0d3589]/[0.08] px-4 py-2.5 text-sm font-bold text-[#0d3589] transition-all active:scale-95 disabled:opacity-70"
    >
      <AnimatePresence mode="wait" initial={false}>
        {done ? (
          <motion.span
            key="done"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="flex items-center gap-2 text-emerald-600"
          >
            <Check size={16} />
            {isAr ? "تم حفظ البحث وسننبّهك!" : "Saved — we'll alert you!"}
          </motion.span>
        ) : create.isPending ? (
          <motion.span key="load" className="flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" />
            {isAr ? "جارٍ الحفظ..." : "Saving..."}
          </motion.span>
        ) : (
          <motion.span key="idle" className="flex items-center gap-2">
            <BellPlus size={16} className="text-amber-500" />
            {isAr ? "احفظ هذا البحث ونبّهني" : "Save this search & alert me"}
          </motion.span>
        )}
      </AnimatePresence>
    </button>
  );
}
